import React from "react";

import { useReducedMotion } from "@/lib/motion";
import { cn } from "@/lib/utils";
import { SkeletonLine, SkeletonAvatar } from "@/ui/skeleton";
import { colors, spacing, motion } from "@/ui/tokens";
import { semanticTokens } from "@packages/config/theme";

export interface TableSkeletonProps {
  rows?: number;
  columns?: number;
  showAvatar?: boolean;
  className?: string;
}

export function TableSkeleton({
  rows = 8,
  columns = 4,
  showAvatar = false,
  className = ""
}: TableSkeletonProps) {
  const reducedMotion = useReducedMotion();

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Loading table data"
      className={cn(spacing.stack.sm, className)}
      data-testid="table-skeleton"
    >
      {/* Header */}
      <div className="flex gap-4 pb-3 border-b border-border/60">
        {Array.from({ length: columns }, (_, i) => (
          <SkeletonLine
            key={`header-${i}`}
            width={i === 0 ? '25%' : '20%'}
            height="1rem"
            className="flex-1"
          />
        ))}
      </div>

      {/* Rows */}
      <div className={spacing.stack.sm}>
        {Array.from({ length: rows }, (_, rowIndex) => (
          <div key={`row-${rowIndex}`} className="flex items-center gap-4 py-2">
            {Array.from({ length: columns }, (_, colIndex) => (
              <div key={`cell-${rowIndex}-${colIndex}`} className="flex-1">
                {colIndex === 0 && showAvatar ? (
                  <div className="flex items-center gap-3">
                    <SkeletonAvatar size="sm" animate={!reducedMotion} />
                    <SkeletonLine
                      animate={!reducedMotion}
                      width="70%"
                      height="0.875rem"
                    />
                  </div>
                ) : (
                  <SkeletonLine
                    animate={!reducedMotion}
                    width={colIndex === columns - 1 ? '60%' : '85%'}
                    height="0.875rem"
                  />
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export interface ListSkeletonProps {
  items?: number;
  showAvatar?: boolean;
  variant?: 'default' | 'compact';
  className?: string;
}

export function ListSkeleton({
  items = 6,
  showAvatar = true,
  variant = "default",
  className = ""
}: ListSkeletonProps) {
  const isCompact = variant === 'compact';
  const reducedMotion = useReducedMotion();

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Loading list data"
      className={cn(spacing.stack.sm, className)}
      data-testid="list-skeleton"
    >
      {Array.from({ length: items }, (_, i) => (
        <div
          key={`item-${i}`}
          className={cn(
            'flex items-center gap-3',
            isCompact
              ? 'py-2'
              : cn(spacing.inset.sm, spacing.radius.lg, colors.surfaces.card)
          )}
        >
          {showAvatar && (
            <SkeletonAvatar
              size={isCompact ? 'sm' : 'md'}
              animate={!reducedMotion && !isCompact}
            />
          )}

          <div className="flex-1 space-y-2">
            <div className={cn(
              isCompact ? "h-3.5" : "h-4",
              "w-40",
              semanticTokens.bg.muted,
              semanticTokens.radius.md,
              (!reducedMotion && !isCompact) && "animate-pulse"
            )} />
            {!isCompact && (
              <div className={cn(
                "h-3 w-32",
                semanticTokens.bg.muted,
                semanticTokens.radius.md,
                !reducedMotion && "animate-pulse"
              )} />
            )}
          </div>

          <SkeletonLine
            animate={!reducedMotion && !isCompact}
            width="80px"
            height={isCompact ? '0.75rem' : '0.875rem'}
          />
        </div>
      ))}
    </div>
  );
}

export function ChartSkeleton({
  height = '300px',
  className = ''
}: {
  height?: string;
  className?: string;
}) {
  const reducedMotion = useReducedMotion();

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Loading chart data"
      className={cn(
        spacing.radius.lg,
        colors.surfaces.card,
        spacing.inset.lg,
        className
      )}
      data-testid="chart-skeleton"
    >
      {/* Chart title */}
      <div className="mb-4">
        <div className={cn(
          "h-5 w-40",
          semanticTokens.bg.muted,
          semanticTokens.radius.md,
          !reducedMotion && "animate-pulse"
        )} />
      </div>

      {/* Chart area */}
      <div
        className={cn(
          'w-full rounded-md',
          colors.surfaces.skeleton,
          !reducedMotion ? motion.animations.pulse : null
        )}
        style={{ height }}
      />

      {/* Legend */}
      <div className="mt-4 flex gap-6">
        {Array.from({ length: 3 }, (_, i) => (
          <div key={`legend-${i}`} className="flex items-center gap-2">
            <div className={cn('w-3 h-3 rounded', colors.surfaces.skeleton)} />
            <SkeletonLine animate={!reducedMotion} width="60px" height="0.875rem" />
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// PAGE SKELETON — Canon v1.4.1 Wallet-Page Pattern
// =============================================================================
// Use this for full page loading states. Mimics exact content structure.
// Key principle: Skeleton shape MUST match final content shape (no CLS).
// =============================================================================

export interface PageSkeletonProps {
  /** Variant determines the layout structure */
  variant?: 'dashboard' | 'wallet' | 'settings' | 'list' | 'detail';
  /** Show header skeleton */
  showHeader?: boolean;
  /** Show action buttons skeleton */
  showActions?: boolean;
  /** Number of content sections */
  sections?: number;
  className?: string;
}

/**
 * PageSkeleton — Canon-compliant page loading skeleton
 *
 * @example
 * // Dashboard page loading
 * {isLoading ? (
 *   <PageSkeleton variant="dashboard" showHeader showActions />
 * ) : (
 *   <DashboardContent />
 * )}
 *
 * @example
 * // Wallet page loading (Revolut-style)
 * {isLoading ? (
 *   <PageSkeleton variant="wallet" showHeader />
 * ) : (
 *   <WalletContent />
 * )}
 */
export function PageSkeleton({
  variant = 'dashboard',
  showHeader = true,
  showActions = false,
  sections = 2,
  className = '',
}: PageSkeletonProps) {
  const reducedMotion = useReducedMotion();
  const animate = !reducedMotion;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Loading page content"
      className={cn("space-y-8", className)}
      data-testid="page-skeleton"
    >
      {/* Header Skeleton */}
      {showHeader && (
        <div className="space-y-2">
          <div className={cn("h-8 w-40", semanticTokens.bg.muted, semanticTokens.radius.md, animate && "animate-pulse")} />
          <div className={cn("h-4 w-64", semanticTokens.bg.muted, semanticTokens.radius.md, animate && "animate-pulse")} />
        </div>
      )}

      {/* Variant-specific content */}
      {variant === 'wallet' && (
        <>
          {/* Balance Card Skeleton — exact WalletPage pattern */}
          <div className={cn("space-y-4", semanticTokens.bg.surface, semanticTokens.radius.lg, semanticTokens.shadow.md, "p-6")}>
            <div className={cn("h-6 w-32", semanticTokens.bg.muted, semanticTokens.radius.md, animate && "animate-pulse")} />
            <div className={cn("h-12 w-48", semanticTokens.bg.muted, semanticTokens.radius.md, animate && "animate-pulse")} />
            <div className={cn("h-4 w-24", semanticTokens.bg.muted, semanticTokens.radius.md, animate && "animate-pulse")} />
          </div>

          {/* Actions Skeleton — mimics action buttons */}
          {showActions && (
            <div className="flex flex-wrap justify-center gap-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className={cn("h-11 w-32", semanticTokens.bg.muted, semanticTokens.radius.md, animate && "animate-pulse")} />
              ))}
            </div>
          )}

          {/* Transactions Skeleton — mimics TransactionHistory */}
          <div className="space-y-4">
            <div className={cn("h-6 w-40", semanticTokens.bg.muted, semanticTokens.radius.md, animate && "animate-pulse")} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className={cn("h-24", semanticTokens.bg.surface, semanticTokens.radius.lg, semanticTokens.shadow.sm, "p-4", animate && "animate-pulse")}>
                  <div className={cn("h-4 w-24 mb-2", semanticTokens.bg.muted, semanticTokens.radius.md)} />
                  <div className={cn("h-6 w-32 mb-2", semanticTokens.bg.muted, semanticTokens.radius.md)} />
                  <div className={cn("h-3 w-20", semanticTokens.bg.muted, semanticTokens.radius.md)} />
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {variant === 'dashboard' && (
        <>
          {/* Stats Cards Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className={cn(
                "p-4 space-y-3",
                colors.surfaces.card,
                spacing.radius.lg,
                "shadow-sm"
              )}>
                <div className={cn("h-4 w-24", semanticTokens.bg.muted, semanticTokens.radius.md, animate && "animate-pulse")} />
                <div className={cn("h-8 w-32", semanticTokens.bg.muted, semanticTokens.radius.md, animate && "animate-pulse")} />
              </div>
            ))}
          </div>

          {/* Main Content Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SectionSkeleton sections={1} linesPerSection={4} showActions={showActions} />
            <SectionSkeleton sections={1} linesPerSection={4} showActions={false} />
          </div>
        </>
      )}

      {variant === 'settings' && (
        <div className="space-y-6 max-w-2xl">
          {Array.from({ length: sections }, (_, i) => (
            <div key={i} className={cn(
              "p-6 space-y-4",
              colors.surfaces.card,
              spacing.radius.lg,
              "shadow-sm"
            )}>
              <div className={cn("h-5 w-32", semanticTokens.bg.muted, semanticTokens.radius.md, animate && "animate-pulse")} />
              <div className="space-y-3">
                <div className={cn("h-10 w-full", semanticTokens.bg.muted, semanticTokens.radius.md, animate && "animate-pulse")} />
                <div className={cn("h-10 w-full", semanticTokens.bg.muted, semanticTokens.radius.md, animate && "animate-pulse")} />
              </div>
              {showActions && (
                <div className={cn("h-10 w-24", semanticTokens.bg.muted, semanticTokens.radius.lg, animate && "animate-pulse")} />
              )}
            </div>
          ))}
        </div>
      )}

      {variant === 'list' && (
        <ListSkeleton items={8} showAvatar />
      )}

      {variant === 'detail' && (
        <>
          {/* Detail Header */}
          <div className={cn(
            "p-6 space-y-4",
            colors.surfaces.card,
            spacing.radius.lg,
            "shadow-sm"
          )}>
            <div className="flex items-center gap-4">
              <SkeletonAvatar size="lg" animate={animate} />
              <div className="flex-1 space-y-2">
                <div className={cn("h-6 w-48", semanticTokens.bg.muted, semanticTokens.radius.md, animate && "animate-pulse")} />
                <div className={cn("h-4 w-32", semanticTokens.bg.muted, semanticTokens.radius.md, animate && "animate-pulse")} />
              </div>
            </div>
          </div>

          {/* Detail Sections */}
          <SectionSkeleton sections={sections} linesPerSection={3} showActions={showActions} />
        </>
      )}
    </div>
  );
}

export interface SectionSkeletonProps {
  sections?: number;
  linesPerSection?: number;
  showActions?: boolean;
  compact?: boolean;
  className?: string;
}

export function SectionSkeleton({
  sections = 1,
  linesPerSection = 3,
  showActions = true,
  compact = false,
  className = '',
}: SectionSkeletonProps) {
  const reducedMotion = useReducedMotion();
  const sectionPadding = compact ? 'p-4' : spacing.inset.lg;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Loading section content"
      className={cn(spacing.stack.md, className)}
      data-testid="section-skeleton"
    >
      {Array.from({ length: sections }, (_, sectionIndex) => (
        <div
          key={`section-${sectionIndex}`}
          className={cn(
            'rounded-2xl border bg-background shadow-sm',
            sectionPadding,
            spacing.stack.sm
          )}
        >
          <div className="flex items-center justify-between">
            <SkeletonLine
              animate={!reducedMotion}
              width="35%"
              height={compact ? '1rem' : '1.125rem'}
            />
            {showActions && (
              <SkeletonLine
                animate={!reducedMotion}
                width="80px"
                height="0.875rem"
              />
            )}
          </div>

          <div className={cn('space-y-2', compact && 'mt-2')}>
            {Array.from({ length: linesPerSection }, (_, lineIndex) => (
              <SkeletonLine
                key={`section-${sectionIndex}-line-${lineIndex}`}
                animate={!reducedMotion}
                width={`${80 - lineIndex * 10}%`}
                height={compact ? '0.75rem' : '0.9rem'}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}